import { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserRole, RoleRoutes } from '@/types/global.types';
import { ChangePasswordModal } from './ChangePasswordModal';
import { AccountValidationWelcomeModal } from './AccountValidationWelcomeModal';
import { AccountRejectedModal } from './AccountRejectedModal';
// AccountApprovedBanner removed - notification system handles approval messages
import { getValidationStatus } from '@/services/validationService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireTenant?: boolean;
  skipProfileCheck?: boolean; // For profile-related routes
  requireFinance?: boolean; // For finance routes - redirects if finance solution not enabled
}

// Hook to check if Zustand has finished rehydrating from localStorage
const useHydrated = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Check if we have auth data in localStorage
    const hasPersistedAuth = localStorage.getItem('nexedu-auth-storage');
    const hasToken = localStorage.getItem('nexedu_access_token');

    if (hasPersistedAuth || hasToken) {
      // Give Zustand a moment to rehydrate
      const timer = setTimeout(() => setHydrated(true), 50);
      return () => clearTimeout(timer);
    } else {
      // No persisted auth, no need to wait
      setHydrated(true);
      return undefined;
    }
  }, []);

  return hydrated;
};

// Roles that don't require profile completion/validation
const EXEMPT_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.DIRECTOR_GENERAL,
  UserRole.BRANCH_MANAGER,
  UserRole.PEDAGOGICAL_DIRECTOR,
  UserRole.PARENT,
];

// Paths that should always skip profile completion check
const PROFILE_PATHS = ['/profile', '/security'];

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requireTenant = false,
  skipProfileCheck = false,
  requireFinance = false,
}) => {
  const location = useLocation();
  const hydrated = useHydrated(); // Wait for Zustand to rehydrate
  const {
    user,
    isAuthenticated,
    getEffectiveRole,
    updateUserProfile,
    updateProfileSubmissionStatus,
    // Modal states
    welcomeModalDismissed,
    rejectedModalDismissed,
    dismissWelcomeModal,
    dismissRejectedModal,
    checkStatusChange,
    syncSessionFromServer,
  } = useAuthStore();
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(true);
  const [shouldRedirectToProfile, setShouldRedirectToProfile] = useState(false);

  // Status check state - ensures we verify the real status before rendering
  const [isStatusChecked, setIsStatusChecked] = useState(false);

  // Rejection info state
  const [rejectionReason, setRejectionReason] = useState<string | undefined>();
  const [rejectionFields, setRejectionFields] = useState<string[]>([]);

  // Helper to get the profile path for the current user
  const getProfilePath = useCallback(() => {
    if (!user) return '/login';
    const roleRoute = RoleRoutes[user.role];
    if (user.role === UserRole.SUPER_ADMIN) {
      return '/admin/profile';
    }
    if (!user.tenantId) return '/login';
    return roleRoute.replace(':tenantId', user.tenantId) + '/profile';
  }, [user]);

  // Fetch validation status to check for status changes
  const fetchStatusAndCheck = useCallback(async (isInitialCheck = false) => {
    if (!user || EXEMPT_ROLES.includes(user.role)) {
      // Exempt roles - mark as checked immediately
      if (isInitialCheck) setIsStatusChecked(true);
      return;
    }

    try {
      const status = await getValidationStatus();
      if (status.status) {
        // Always update the store with the real status from the server
        if (status.status !== user.profileSubmissionStatus) {
          updateProfileSubmissionStatus(status.status);
          checkStatusChange(status.status);
        }

        // If rejected, store rejection info
        if (status.status === 'REJECTED' && status.validationRequest) {
          setRejectionReason(status.validationRequest.rejectionReason);
          setRejectionFields(status.validationRequest.rejectionFields || []);
        }
      }
    } catch {
      // Silently fail - not critical, but allow rendering with cached status
    } finally {
      // Mark status as checked after initial check
      if (isInitialCheck) setIsStatusChecked(true);
    }
  }, [user, updateProfileSubmissionStatus, checkStatusChange]);

  // Check status on mount (initial) and periodically
  useEffect(() => {
    // Initial check with flag
    fetchStatusAndCheck(true);
    // Periodic checks without flag
    const interval = setInterval(() => fetchStatusAndCheck(false), 60000);
    return () => clearInterval(interval);
  }, [fetchStatusAndCheck]);

  // Re-read the session from the server on mount and after each token
  // refresh. If an admin changed this user's role, switch to the new role's
  // dashboard: the sidebar skeleton and routes are built from the cached role,
  // so without this the user keeps the old role's menus until logout.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    const sync = async (force = false) => {
      const result = await syncSessionFromServer({ force });
      if (cancelled || !result.roleChanged || !result.role) return;
      const roleRoute = RoleRoutes[result.role];
      const tenantId = useAuthStore.getState().user?.tenantId;
      if (result.role === UserRole.SUPER_ADMIN) {
        navigate('/admin/dashboard', { replace: true });
      } else if (roleRoute && tenantId) {
        navigate(roleRoute.replace(':tenantId', tenantId) + '/dashboard', { replace: true });
      }
    };
    const onTokenRefreshed = () => { void sync(true); };
    void sync();
    window.addEventListener('nexedu:token-refreshed', onTokenRefreshed);
    return () => {
      cancelled = true;
      window.removeEventListener('nexedu:token-refreshed', onTokenRefreshed);
    };
    // Run once per session: user identity, not the whole user object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // Auto-skip profile check for profile-related paths
  const isProfilePath = PROFILE_PATHS.some(path => location.pathname.endsWith(path));
  const shouldSkipProfileCheck = skipProfileCheck || isProfilePath;

  // Wait for Zustand to rehydrate from localStorage before making routing decisions
  // This prevents flash redirects during HMR or page refresh
  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for financial suspension (students only)
  // This flag is set by axios interceptor when 403 with financial suspension is received
  if (user.role === UserRole.STUDENT) {
    const isFinanciallySuspended = localStorage.getItem('nexedu_financial_suspended') === 'true';
    if (isFinanciallySuspended) {
      return <Navigate to="/financial-suspended" replace />;
    }
  }

  // Suspended users are redirected to the suspension page
  // They can still login but have restricted access
  if (user.status === 'SUSPENDED') {
    return <Navigate to="/account-suspended" replace />;
  }

  // Check if tenant/institution is suspended (billing issues, etc.)
  // Super Admin is exempt from this check as they need to manage suspended institutions
  if (user.tenantStatus === 'SUSPENDED' && user.role !== UserRole.SUPER_ADMIN) {
    return <Navigate to="/institution-suspended" replace />;
  }

  // Wait for initial status check before rendering (for non-exempt roles)
  // This prevents showing "Profile incomplet" briefly when the status is actually APPROVED
  if (!isStatusChecked && !EXEMPT_ROLES.includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Check if user must change password (parents on first login)
  if (user.mustChangePassword && showChangePassword) {
    return (
      <ChangePasswordModal
        isOpen={true}
        onSuccess={() => {
          // Update user state to remove mustChangePassword flag
          updateUserProfile({ mustChangePassword: false } as any);
          setShowChangePassword(false);
        }}
      />
    );
  }

  // Check role restrictions
  if (allowedRoles && allowedRoles.length > 0) {
    const effectiveRole = getEffectiveRole();
    if (!effectiveRole || !allowedRoles.includes(effectiveRole)) {
      // Redirect to appropriate dashboard based on user's actual role
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check tenant requirement
  if (requireTenant && !user.tenantId) {
    // Super admin without tenant trying to access tenant routes
    if (user.role === UserRole.SUPER_ADMIN) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Check finance solution requirement
  if (requireFinance) {
    // SUPER_ADMIN can always access finance routes (for administration purposes)
    if (user.role !== UserRole.SUPER_ADMIN) {
      const isFinanceEnabled = user.enabledSolutions?.includes('finance') ?? false;
      if (!isFinanceEnabled) {
        // Redirect to dashboard if finance is not enabled for this institution
        const roleRoute = RoleRoutes[user.role];
        const dashboardPath = user.tenantId && roleRoute
          ? roleRoute.replace(':tenantId', user.tenantId) + '/dashboard'
          : '/';
        return <Navigate to={dashboardPath} replace />;
      }
    }
  }

  // Check profile completion requirement
  // Skip for exempt roles and profile-related routes
  if (!shouldSkipProfileCheck && !EXEMPT_ROLES.includes(user.role)) {
    const profileStatus = user.profileSubmissionStatus || 'DRAFT';

    // APPROVED status = full access (notification already handles the approval message)
    if (profileStatus === 'APPROVED') {
      return <>{children}</>;
    }

    // PENDING_VALIDATION = temporary access (allow browsing with all features)
    if (profileStatus === 'PENDING_VALIDATION') {
      return <>{children}</>;
    }

    // REJECTED = show rejection modal, then redirect to profile to fix issues
    if (profileStatus === 'REJECTED') {
      // If modal not dismissed, show it
      if (!rejectedModalDismissed) {
        const handleRejectedConfirm = () => {
          dismissRejectedModal();
          setShouldRedirectToProfile(true);
        };
        return (
          <>
            <AccountRejectedModal
              isOpen={true}
              onConfirm={handleRejectedConfirm}
              rejectionReason={rejectionReason}
              rejectionFields={rejectionFields}
            />
            {children}
          </>
        );
      }
      // After modal dismissed, redirect to profile if not already there
      if (shouldRedirectToProfile && !isProfilePath) {
        return <Navigate to={getProfilePath()} replace />;
      }
      // On profile page or redirect done - allow access
      return <>{children}</>;
    }

    // DRAFT = full access until deadline passes
    // Only restrict if user is blocked due to deadline
    if (profileStatus === 'DRAFT') {
      // Professors (TEACHER or staff who also teach) are never redirected: they
      // keep every feature and see the deadline banner (decision 2026-10-24).
      const isProfessor = user.role === UserRole.TEACHER || user.isAlsoProfessor === true;
      // Check if user is blocked by deadline
      if (user.isProfileDeadlineBlocked && !isProfessor) {
        // User is past deadline, redirect to profile
        if (!isProfilePath) {
          return <Navigate to={getProfilePath()} replace />;
        }
      }
      // Show welcome modal for first-time users (non-blocking)
      if (!welcomeModalDismissed) {
        return (
          <>
            <AccountValidationWelcomeModal
              isOpen={true}
              onConfirm={dismissWelcomeModal}
              userName={user.firstName}
            />
            {children}
          </>
        );
      }
      // Full access for DRAFT users
      return <>{children}</>;
    }
  }

  return <>{children}</>;
};

// Higher-order component version
export const withProtectedRoute = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: { allowedRoles?: UserRole[]; requireTenant?: boolean; requireFinance?: boolean }
) => {
  const WithProtectedRoute: React.FC<P> = (props) => {
    return (
      <ProtectedRoute
        allowedRoles={options?.allowedRoles}
        requireTenant={options?.requireTenant}
        requireFinance={options?.requireFinance}
      >
        <WrappedComponent {...props} />
      </ProtectedRoute>
    );
  };

  WithProtectedRoute.displayName = `WithProtectedRoute(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithProtectedRoute;
};
